import { SimCore, type RacerInput, type SimEvent } from '@/sim/SimCore';
import type { SimRacer } from '@/sim/SimRacer';
import { generateTrack } from '@/world/Track';
import { CITIES, courseExtras } from '@/config/cities';
import { getPlayerVehicle } from '@/entities/vehicles';
import { tuning, type DifficultyLevel } from '@/config/tuning';
import { ClientSession, type RacerPose } from '@/net/ClientSession';
import type { NetDriver } from '@/net/NetDriver';
import {
  decodeSnapshot,
  decodeWtPing,
  encodeInputBatch,
  encodeWtHello,
  encodeWtPing,
  isSnapshotFrame,
  quantizeInput,
  INPUT_BATCH_SIZE,
  INPUT_SEND_EVERY,
  type InputBatchEntry,
} from '@/net/codec';
import { ClockSync, initialLeadTicks } from '@/net/clocksync';
import { netdiag, resetNetdiag } from '@/net/netdiag';
import { applySnapshot } from '@/net/snapshot';
import type { Snapshot } from '@/net/protocol';

/** The server's join handshake — tells the client which race to build + own. */
export interface Welcome {
  racerId: number;
  city: string;
  seed: number;
  difficulty: DifficultyLevel;
  fieldSize: number;
  /** Which slots are human-occupied (stock engine) vs AI — must match the host. */
  humanSlots: number[];
  /** Online tour (M34): 0 = single race; >0 = race `raceNum` of a `tourLen` tour. */
  tourLen?: number;
  raceNum?: number;
  /** M81: per-slot equipped cosmetics as serialized loadout strings ('' =
   *  stock). Optional — an older worker simply doesn't send them. */
  looks?: string[];
  /** M110: per-slot REAL display names (Racer ID usernames / guest nicknames;
   *  '' = none → the client falls back to the deterministic pool handle).
   *  Optional — an older server simply doesn't send them. */
  names?: string[];
  /** M105: the room's phase at welcome time ('lobby' | 'staging' | 'racing' |
   *  …). Optional — an older worker doesn't send it. Game uses it to tell a
   *  mid-race rejoin apart from a room that restarted and lost the race. */
  phase?: string;
  /** M143c: which of the room's races this is (the room counts them from 1).
   *  Lets a rejoin tell "the race I was in" from "a new one that began
   *  without me". Optional — an older server doesn't send it. */
  seq?: number;
  /** M143c: an online tour's official results so far, oldest first (places
   *  keyed by racer slot). Optional — absent outside a tour / on an older
   *  server. */
  tourResults?: { city?: string; places?: number[] | Record<string, number> }[];
}

/** V2 Phase C (M108): how to reach this room's WebTransport datagram lane. */
export interface WtLaneConfig {
  /** https:// origin the lane lives on (the app-terminated QUIC endpoint). */
  url: string;
  /** The room code + seat token the hello datagram binds with. */
  room: string;
  tok: string;
  /** Dev only (?wthash= alongside ?debug=1): a base64 SHA-256 cert hash for a
   *  local self-signed endpoint. Production uses real certs — never this. */
  certHashB64?: string;
}

/** The browser WebTransport surface the lane uses (typed structurally — lib
 *  DOM types for WT are still uneven across TS versions).
 *
 *  M111d — TWO datagram API generations exist in the wild: Chromium's
 *  original `datagrams.writable` property, and the CURRENT spec's
 *  `datagrams.createWritable()` which is the ONLY send surface WebKit ships
 *  (verified in WebKit's WebTransportDatagramDuplexStream.idl — there is no
 *  `writable` attribute at all). This single missing property is why every
 *  real-iPhone race showed wtSnaps 0: the old code threw on
 *  `datagrams.writable.getWriter()`, the throw was swallowed, the hello
 *  never went out, and the server's 5 s unbound timeout closed the session
 *  (the ~15-20 s session-create loop in the Fly logs). `readable` is an
 *  attribute in BOTH generations. */
interface WtLike {
  ready: Promise<void>;
  closed: Promise<unknown>;
  reliability?: string;
  datagrams: {
    readable: ReadableStream<Uint8Array>;
    writable?: WritableStream<Uint8Array>;
    createWritable?: () => WritableStream<Uint8Array>;
    maxDatagramSize?: number;
  };
  close(): void;
}

/** WT lane promotion: how many WT snapshots prove the downlink before the WS
 *  copies are switched off, and how long a silent lane keeps its promotion. */
const WT_PROMOTE_AFTER_SNAPS = 5;
/** M143: a confirmed lane that has delivered no FRESH snapshot for this long
 *  is demoted (the WebSocket copies resume). It was 700 ms of TOTAL silence,
 *  and any datagram at all — a stale or duplicate one — reset the timer, so a
 *  lane trickling a few datagrams a second kept the WebSocket copies switched
 *  off and the player on a few snapshots a second. Seven missed in a row. */
const WT_DEMOTE_SILENCE_MS = 350;
/** M143: the datagrams that prove a lane before promotion must arrive at a
 *  healthy cadence — a longer gap restarts the count (a flapping lane must
 *  not keep re-promoting itself). */
const WT_PROMOTE_MAX_GAP_MS = 160;
const WT_RETRY_MS = 15_000;
const WT_HELLO_EVERY_MS = 400;
/** M108b: how often a confirmed lane RENEWS its server-side lease (the
 *  server suppresses WS snapshot copies only while the lease is live, so a
 *  client that stops hearing its own downlink — or dies with its WS — can
 *  never leave the server writing into a void). Must be well under
 *  RoomCore's WT_LEASE_MS (2.5 s). */
const WT_HEARTBEAT_MS = 1_000;
/** M143: how often the lane measures its own round trip (a datagram echo). */
const WT_PING_MS = 1_000;
/** M143: a clock advance of more than this many ticks at once counts as a BIG
 *  move in the diagnostics (smaller ones are routine buffer growth). */
const BIG_ADVANCE_TICKS = 8;
/** M143: margin feedback is only meaningful while OUR loop is running — a
 *  hidden tab or a frozen frame sends no inputs, and the "late" readings that
 *  produces describe the freeze, not the link. */
const LOOP_LIVE_MS = 120;
/** M143: a snapshot more than this many ticks behind our clock is STALE (it
 *  sat in a stalled TCP stream, or behind a radio blackout). Reconciling it
 *  means re-simulating everything since — the right answer, but expensive —
 *  and a stalled stream delivers a whole run of them. So deep reconciles are
 *  rationed: at most one per DEEP_REPLAY_MIN_MS; a fresher snapshot arriving
 *  meanwhile simply replaces the stale one. */
const DEEP_REPLAY_TICKS = 72;
const DEEP_REPLAY_MIN_MS = 200;
/** M143c: "behind our clock" is measured against how far behind a FRESH
 *  snapshot normally is on this link (a slowly rising floor under the
 *  arrivals). The first cut compared against zero, so on a slow link — about
 *  half a second of round trip — every snapshot counted as stale and the
 *  whole race was reconciled five times a second instead of twenty. */
const DEPTH_FLOOR_RISE = 0.25; // ticks per snapshot ≈ 5 ticks a second

/**
 * WsDriver.ts — the client connected to a REAL host over a WebSocket (the
 * regional Fly room servers in production, the Cloudflare DO as fallback).
 * Builds its prediction sim from the server's `welcome`, waits in the lobby
 * until the host starts the race (first snapshot), then predicts the local
 * car each tick and reconciles to the host's snapshots.
 *
 * M69 — the client CLOCK-SYNCS to run ~one RTT ahead of the server, so every
 * input arrives before the server simulates its tick (scheduled inputs — see
 * AuthoritativeRoom + clocksync.ts).
 *
 * M143 — the clock is an ADAPTIVE INPUT BUFFER (clocksync.ts): it leads by as
 * much as this link's worst recent moments need, grows at once, gives back
 * slowly, and never acts on feedback older than its own last move. Its tick
 * labels only ever move FORWARD (extra ticks are simulated, a long freeze
 * skips labels) — the old "hard resync" that wiped the prediction history and
 * the input ring, and could rewind labels, is gone. Snapshots are reconciled
 * at most once per tick (the newest wins), and the lost time of a frozen
 * frame is re-simulated the moment the loop reports it.
 *
 * V2 Phase C (M108) — the WEBTRANSPORT DATAGRAM LANE: once the race is live,
 * the driver quietly opens a WT session to the same host and, when the lane
 * PROVES itself (session ready + genuinely-unreliable transport + real
 * snapshots arriving), tells the server to stop duplicating snapshots onto
 * the WS. Inputs are DUAL-SENT on both channels while the lane lives (the
 * server dedupes by tick — loss insurance, never duplication). The WS remains
 * the control channel and the instant fallback: a quiet lane demotes itself
 * and WS snapshots resume seamlessly. Join time can never regress — the WS
 * connects first, exactly as before, and the lane only ever accelerates.
 */
/** Event types the prediction sim owns for the LOCAL racer (see takeEvents). */
const LOCAL_OWN_TYPES = new Set(['pad', 'itemLand', 'collect', 'fire', 'deny', 'ballHit']);
export function isLocallyPredicted(e: SimEvent, localId: number): boolean {
  if (e.type === 'countdown') return true;
  return 'racerId' in e && e.racerId === localId && LOCAL_OWN_TYPES.has(e.type);
}

export class WsDriver implements NetDriver {
  readonly client: ClientSession;
  private readonly localId: number;
  private readonly dtMs: number;
  private pendingEvents: SimEvent[] = [];
  /** False until the host starts the match (first snapshot) — we hold prediction
   *  in the lobby so the client doesn't run ahead of a not-yet-stepping server. */
  private started = false;
  status: string | null = 'Connecting…';
  /** Smoothed round-trip time (ms) for the HUD ping pill, or null pre-measure:
   *  the datagram lane's own once it carries the race (M143), else the
   *  WebSocket's. */
  get rtt(): number | null {
    return this.wtState === 'confirmed' && this.wtRtt !== null ? this.wtRtt : this.wsRtt;
  }
  /** The link's best measured round trip (see `rttHint`). */
  get rttFloor(): number | null {
    return this.rttMin;
  }
  /** When the last snapshot was APPLIED (either channel) — Game's link
   *  watchdog reads this via NetRace so WT-only streaming never looks silent. */
  lastSnapAt = 0;
  /** Ring of the last N quantized inputs — every batch resends them all, so a
   *  lost/late packet can't eat an input edge (M52 redundancy). MUST hold
   *  consecutive ticks (encodeInputBatch encodes only the first tick) — a
   *  label skip therefore FLUSHES it (sent as-is, so no unsent edge is lost)
   *  and starts a fresh one. */
  private readonly inputRing: InputBatchEntry[] = [];
  private lastPingAt = 0;
  private lastSnapApplied = 0;
  /** Newest RECEIVED snapshot tick — the cross-channel dedupe guard (M108): a
   *  WS copy arriving after its WT twin (or vice versa) must never count twice. */
  private lastAppliedTick = -1;
  /** The adaptive input buffer (M143) — see clocksync.ts. */
  private readonly clock = new ClockSync();
  /** MIN-filtered RTT with slow upward aging (M73) — seats the clock once. */
  private rttMin: number | null = null;
  /** The newest snapshot received but not yet reconciled (M143). Reconciling
   *  inside the socket handler meant a burst of N queued snapshots (after a
   *  device freeze, a Wi-Fi hop, or always on Safari, whose datagram queue is
   *  unbounded) cost N full replays in a row — on the very frame that was
   *  already late. Only the newest matters; it is applied once, at the next
   *  tick. */
  private pendingSnap: Snapshot | null = null;
  /** When tick() last ran (see LOOP_LIVE_MS); 0 until the first tick. */
  private lastTickAt = 0;
  /** When this driver was built: the wait for its FIRST tick is a pause too. */
  private readonly createdAt = performance.now();
  /** When our own loop last came back from a pause longer than LOOP_LIVE_MS
   *  (M143c). A ping that was in flight across that pause measured the pause,
   *  not the link. */
  private loopStallEnd = -Infinity;
  /** WebSocket ping round trip (smoothed, ms) and the lane's own (M143). */
  private wsRtt: number | null = null;
  private wtRtt: number | null = null;
  private wtPingAt = 0;
  /** When the last deep (stale-snapshot) reconcile ran — see DEEP_REPLAY. */
  private lastDeepAt = -Infinity;
  /** How many ticks behind our clock a fresh snapshot arrives on this link
   *  (see DEPTH_FLOOR_RISE). Infinity until the first one is measured. */
  private depthFloor = Infinity;
  /** When the clock was seated (M143c) — lost time from BEFORE that moment is
   *  not ours to make up (see absorbDroppedTicks). */
  private seatedAt = 0;

  // ---- WT lane state (M108) ----
  private wt: WtLike | null = null;
  private wtWriter: WritableStreamDefaultWriter<Uint8Array> | null = null;
  private wtState: 'off' | 'trying' | 'live' | 'confirmed' = 'off';
  /** When the lane last delivered a FRESH snapshot (demotion clock) and any
   *  datagram snapshot at all (promotion cadence). */
  private wtSnapAt = 0;
  private wtAnyAt = 0;
  private wtSnapsSincePromote = 0;
  private wtRetryAt = 0;
  /** M108b: when the confirmed lane last renewed its server-side lease. */
  private wtHeartbeatAt = 0;
  private wtHelloTimer: ReturnType<typeof setInterval> | null = null;
  private disposed = false;

  /** Kept so dispose can detach it — the ws OUTLIVES this driver (Game owns it and
   *  reuses it across a rematch), so a stale driver must stop listening. */
  private readonly onMsg = (e: MessageEvent): void => this.onMessage(e);

  constructor(
    private readonly ws: WebSocket,
    welcome: Welcome,
    dt: number,
    seed?: Snapshot,
    private readonly wtConfig?: WtLaneConfig,
    /** M143c: the round trip the PREVIOUS driver on this connection measured.
     *  A driver is rebuilt for every race, moments before the race's first
     *  snapshot seats the clock, and right after the page's heaviest work (a
     *  city build, the first frame's shader compile), so its own first sample
     *  is usually not in yet. Without this the clock was seated from a guess. */
    rttHint?: number | null,
  ) {
    this.dtMs = dt * 1000;
    if (typeof rttHint === 'number' && rttHint >= 0) {
      this.rttMin = rttHint;
      this.wsRtt = rttHint;
    }
    const city = CITIES.find((c) => c.id === welcome.city) ?? CITIES[0];
    const track = generateTrack(welcome.seed, city.track);
    const sim = new SimCore(
      track,
      welcome.difficulty,
      courseExtras(city),
      welcome.seed,
      welcome.fieldSize,
      welcome.humanSlots,
    );
    // M51: our own slot drives OUR ride from the first predicted tick; every
    // other slot's vehicle arrives with the first snapshot (the room is the
    // authority — it read ours from the join URL, so these agree).
    sim.setVehicle(welcome.racerId, getPlayerVehicle());
    // M105: a mid-race rejoin passes the world we were ALREADY rendering —
    // start the fresh prediction sim THERE, not on the start grid, so the
    // driver-swap frame doesn't teleport every car back to the line while
    // prediction holds for the first snapshot of the new connection.
    if (seed) {
      applySnapshot(sim, seed);
      // M143: …and tell the render so. Each car and ball remembers its
      // PREVIOUS pose for frame interpolation, and a freshly built sim's
      // previous pose is the start grid — so until the first snapshot of the
      // new connection arrived, every frame drew the whole field somewhere on
      // the line between the grid and where it really was (measured in a
      // two-browser race: the player's car jumped 302 units and back).
      for (const r of sim.racers) {
        r.car.settleRenderPose();
        r.ball.settleRenderPose();
      }
    }
    this.localId = welcome.racerId;
    // 'label': the real servers apply each input at the tick it is labelled for.
    this.client = new ClientSession(sim, welcome.racerId, dt, 'label');
    this.status = 'Waiting for opponent…';
    ws.addEventListener('message', this.onMsg);
    resetNetdiag(); // fresh diagnostics per race (M73)
    this.sendPing(); // prime the RTT estimate BEFORE the race starts (M69: the
    // first snapshot's clock resync needs it for the initial lead)
  }

  private sendPing(): void {
    if (this.ws.readyState !== WebSocket.OPEN) return;
    this.lastPingAt = performance.now();
    this.ws.send(JSON.stringify({ t: 'ping', ts: Math.round(this.lastPingAt) }));
  }

  /** Seat the clock when the race's first snapshot arrives: nothing has been
   *  sent yet, so this is the one moment a label may be set freely. From here
   *  on the clock only moves by the controller's two actions (extra ticks /
   *  one skipped tick) and by time itself. */
  private seatClock(snapTick: number): void {
    const lead = initialLeadTicks(this.rttMin ?? this.wsRtt, this.dtMs);
    this.client.predictor.seat(snapTick + lead);
    this.inputRing.length = 0;
    this.seatedAt = performance.now();
  }

  /** ONE snapshot intake for BOTH channels (M108): cross-channel tick dedupe,
   *  arrival cadence, clock feedback, diagnostics. The reconcile itself waits
   *  for the next tick (M143 — see `pendingSnap`). */
  private onSnapshot(snap: Snapshot, viaWt: boolean): void {
    // LANE HEALTH IS COUNTED FIRST (M108b), before the cross-channel dedupe:
    // while the server is still dual-sending, the WS twin usually wins the
    // race (TCP is quick on a good link), so counting only the datagrams
    // that ARRIVE FIRST meant the promote threshold was almost never
    // reached — the lane stayed unpromoted, the server kept dual-sending,
    // and the deadlock held. A datagram that arrives at all proves the
    // downlink works, which is the only thing promotion should mean.
    if (viaWt) {
      netdiag.wtSnaps++;
      const at = performance.now();
      if (this.wtState === 'trying') this.wtState = 'live';
      if (this.wtState === 'live') {
        // Only a steady run proves the lane (M143).
        if (at - this.wtAnyAt > WT_PROMOTE_MAX_GAP_MS) this.wtSnapsSincePromote = 0;
        this.wtSnapsSincePromote++;
        if (this.wtSnapsSincePromote >= WT_PROMOTE_AFTER_SNAPS) {
          this.wtSnapAt = at;
          this.promoteWt();
        }
      }
      this.wtAnyAt = at;
    }
    if (snap.tick <= this.lastAppliedTick) return; // stale twin / out-of-order
    this.lastAppliedTick = snap.tick;
    // A FRESH snapshot over the lane is what keeps a confirmed lane confirmed.
    if (viaWt) this.wtSnapAt = performance.now();
    if (!this.started) {
      this.started = true;
      this.status = null;
      this.seatClock(snap.tick); // start the clock ahead of the server
      this.startWtLane(); // the race is live — try the accelerator (M108)
    }
    const now = performance.now();
    const gapMs = netdiag.snaps > 0 ? now - this.lastSnapApplied : 50;
    this.lastSnapApplied = now;
    this.lastSnapAt = now;
    if (netdiag.snaps > 0) {
      netdiag.snapGapLastMs = gapMs;
      if (gapMs > netdiag.snapGapMaxMs) netdiag.snapGapMaxMs = gapMs;
    }
    netdiag.snaps++;
    // Clock feedback — ONLY from snapshots on a live cadence (M73: a
    // post-stall burst drains back-to-back and its margins describe the
    // past) and ONLY while our own loop is running (M143: a frozen frame or
    // a hidden tab sends nothing, and that is not the link's doing).
    const margin = snap.margins[this.localId];
    if (typeof margin === 'number') {
      netdiag.marginLast = margin;
      if (gapMs > 20 && now - this.lastTickAt < LOOP_LIVE_MS) {
        // …and the same live arrivals say how stale "fresh" is on this link.
        const depth = this.client.predictor.clientTick - snap.tick;
        if (depth >= 0) this.depthFloor = Math.min(depth, this.depthFloor + DEPTH_FLOOR_RISE);
        if (this.clock.sample(margin, snap.tick, now)) {
          if (margin < netdiag.marginMin) netdiag.marginMin = margin;
          if (margin > netdiag.marginMax) netdiag.marginMax = margin;
        }
        const e = this.clock.estimate();
        if (e) {
          netdiag.marginWorst = e.worst;
          netdiag.marginTyp = e.typical;
        }
      }
    }
    const acked = snap.ack[this.localId];
    if (typeof acked === 'number') {
      const lag = Math.max(0, snap.tick - 1 - acked);
      netdiag.ackLagLast = lag;
      if (lag > netdiag.ackLagMax) netdiag.ackLagMax = lag;
      if (lag > 0) netdiag.ackLagged++;
    }
    this.pendingSnap = snap; // the newest wins; reconciled at the next tick
  }

  private onMessage(e: MessageEvent): void {
    // The hot path is BINARY (M52): snapshots arrive as ArrayBuffers.
    if (e.data instanceof ArrayBuffer) {
      if (!isSnapshotFrame(e.data)) return;
      // Guarded like the WT reader: a truncated/malformed frame must never
      // throw out of the socket handler and kill the net loop (M108b).
      try {
        this.onSnapshot(decodeSnapshot(e.data), false);
      } catch {
        /* undecodable frame — drop it; the next snapshot resyncs */
      }
      return;
    }
    let m: { t: string; events?: SimEvent[]; humans?: number; cap?: number; ts?: number };
    try {
      m = JSON.parse(typeof e.data === 'string' ? e.data : '');
    } catch {
      return;
    }
    if (m.t === 'ev' && m.events) {
      this.pendingEvents.push(...m.events);
    } else if (m.t === 'pong' && typeof m.ts === 'number') {
      // M143: only while our own loop is running. A pong that sat in the queue
      // behind a busy main thread (a city build, a frozen frame, a hidden tab)
      // measures THAT, not the link — it used to put "507 ms" on the ping pill
      // at the start of a race and seat the clock from it (seen in a
      // two-browser tour on a 5 ms link).
      // M143c: ...and not if the loop paused at ANY point while this ping was
      // out. The first cut only asked whether the loop was alive when the
      // pong was READ; after a pause the loop ticks first and the queued pong
      // is read next, so it passed. Measured again in a two-browser tour: a
      // 413 ms "round trip" on a 5 ms link, the clock seated 72 ticks ahead
      // (the most it allows), and corrections of 20 units for the first eight
      // seconds of the race while it worked its way back.
      const at = performance.now();
      // (A ping sent in the very tick that ended a pause is sound: compare
      // against its exact send time when it is the newest one, since the
      // echoed stamp is rounded.)
      const sentAt = Math.round(this.lastPingAt) === m.ts ? this.lastPingAt : m.ts + 0.5;
      if (at - this.lastTickAt > LOOP_LIVE_MS || this.loopStallEnd > sentAt) return;
      const sample = Math.max(0, at - m.ts);
      this.wsRtt = this.wsRtt === null ? sample : this.wsRtt * 0.7 + sample * 0.3;
      this.rttMin = this.rttMin === null ? sample : Math.min(this.rttMin + 2, sample);
    } else if (m.t === 'lobby') {
      this.status = `Waiting for players… (${m.humans ?? 1}/${m.cap ?? 2})  ·  share this link`;
    }
  }

  // ---- WT lane machine (M108) ----

  /** Open the datagram lane in the background. Never blocks or delays the WS
   *  path; failure of any kind leaves the game exactly where it was. */
  private startWtLane(): void {
    if (this.wtState !== 'off' || this.disposed) return;
    const cfg = this.wtConfig;
    if (!cfg) return;
    const WT = (globalThis as { WebTransport?: new (url: string, o?: unknown) => WtLike })
      .WebTransport;
    if (!WT) {
      netdiag.wtStage = 'no-api'; // no browser support — WS carries everything
      return;
    }
    this.wtState = 'trying';
    this.wtSnapsSincePromote = 0;
    let wt: WtLike;
    try {
      // requireUnreliable (M143): Safari honours it by NOT falling back to
      // HTTP/2-over-TCP — a "lane" that is really TCP would be no better than
      // the WebSocket we already have. Chrome has no such fallback and
      // ignores the option. (congestionControl is deliberately not set: no
      // shipping browser implements a low-latency algorithm for it.)
      const opts: Record<string, unknown> = { requireUnreliable: true };
      if (cfg.certHashB64) {
        opts.serverCertificateHashes = [
          {
            algorithm: 'sha-256',
            value: Uint8Array.from(atob(cfg.certHashB64), (c) => c.charCodeAt(0)).buffer,
          },
        ];
      }
      wt = new WT(`${cfg.url}/dgram`, opts);
      netdiag.wtStage = 'ctor';
    } catch {
      netdiag.wtStage = 'ctor-fail';
      this.wtState = 'off';
      this.wtRetryAt = performance.now() + WT_RETRY_MS;
      return;
    }
    this.wt = wt;
    const timeout = setTimeout(() => wt.close(), 4000); // a hung handshake dies
    wt.ready.then(
      () => {
        clearTimeout(timeout);
        if (this.disposed || this.wt !== wt) return void wt.close();
        netdiag.wtStage = 'ready';
        // M111d: the whole post-ready setup is guarded — a sync throw in
        // here used to be silently swallowed by the promise chain, leaving
        // the lane wedged in 'trying' forever (the iOS failure mode).
        try {
          // A reliable-only session is Safari's HTTP/2-over-TCP fallback —
          // no better than the WS we already have. Chromium omits the
          // attribute entirely; the spec's transitional 'pending' is
          // accepted (a real QUIC session may still be settling its mode) —
          // the diag stage records whatever we saw either way.
          if (wt.reliability === 'reliable-only') {
            netdiag.wtStage = `rel:${wt.reliability}`;
            wt.close();
            return;
          }
          // M111d — BOTH datagram API generations: WebKit (iPhone/iPad/Mac
          // Safari) ships ONLY the current spec's createWritable(); Chromium
          // still exposes the original `writable` property (and newer builds
          // have both — prefer the spec surface).
          const writable = wt.datagrams.createWritable?.() ?? wt.datagrams.writable;
          if (!writable) {
            netdiag.wtStage = 'no-writable';
            wt.close();
            return;
          }
          this.wtWriter = writable.getWriter();
          void this.readWtDatagrams(wt);
          // Hello until the downlink answers (the server binds our seat by
          // the same token that already grants seat reclaim — one trust
          // model).
          const hello = new Uint8Array(encodeWtHello(cfg.room, cfg.tok));
          this.wtHelloTimer = setInterval(() => {
            if (this.wtState !== 'trying') {
              if (this.wtHelloTimer) clearInterval(this.wtHelloTimer);
              this.wtHelloTimer = null;
              return;
            }
            this.wtWriter?.write(hello).catch(() => {
              /* lane death is handled by `closed` */
            });
          }, WT_HELLO_EVERY_MS);
          netdiag.wtStage = 'hello';
          this.wtWriter.write(hello).catch(() => {
            /* ditto */
          });
        } catch {
          netdiag.wtStage = 'hello-fail';
          wt.close();
        }
      },
      () => {
        clearTimeout(timeout);
        if (netdiag.wtStage === 'ctor') netdiag.wtStage = 'ready-fail';
        if (this.wt === wt) this.teardownWt('failed');
      },
    );
    // Both outcomes (M143): by spec `closed` REJECTS on an abrupt close or a
    // failed handshake. With only a fulfilment handler the lane was never
    // torn down (stuck "live", writing 30 batches a second into a dead
    // writer, never retried) and every failure raised an unhandled rejection.
    const onClosed = (): void => {
      if (this.wt === wt) this.teardownWt('closed');
    };
    wt.closed.then(onClosed, onClosed);
  }

  private async readWtDatagrams(wt: WtLike): Promise<void> {
    let reader: ReadableStreamDefaultReader<Uint8Array>;
    try {
      reader = wt.datagrams.readable.getReader();
    } catch {
      return; // lane death is handled by `closed`
    }
    for (;;) {
      let value: Uint8Array | undefined;
      try {
        const r = await reader.read();
        if (r.done || this.wt !== wt) break;
        value = r.value;
      } catch {
        break; // the stream ended — lane death is handled by `closed`
      }
      // One bad datagram must not end the lane (M143): each is handled on its
      // own, exactly like a bad WebSocket frame.
      try {
        if (netdiag.wtStage === 'hello') netdiag.wtStage = 'data'; // downlink proven
        const buf = value.buffer.slice(
          value.byteOffset,
          value.byteOffset + value.byteLength,
        ) as ArrayBuffer;
        if (isSnapshotFrame(buf)) {
          this.onSnapshot(decodeSnapshot(buf), true);
        } else {
          const ts = decodeWtPing(buf); // our own ping, echoed (M143)
          const at = performance.now();
          // (Only while our loop ran the whole time — see the WebSocket pong handler.)
          if (ts !== null && at - this.lastTickAt <= LOOP_LIVE_MS && this.loopStallEnd <= ts + 0.5) {
            const sample = Math.max(0, at - ts);
            this.wtRtt = this.wtRtt === null ? sample : this.wtRtt * 0.7 + sample * 0.3;
            netdiag.rttWt = this.wtRtt;
          }
        }
      } catch {
        /* undecodable datagram — drop it; the next one stands alone */
      }
    }
  }

  /** The downlink is proven — lease the suppression of the WS copies. */
  private promoteWt(): void {
    if (this.wtState !== 'live') return;
    this.wtState = 'confirmed';
    netdiag.wtActive = 1;
    netdiag.wtPromotions++;
    this.sendWtHeartbeat();
  }

  /** M108b: renew (or open) the server-side lease. Called on promote and
   *  every WT_HEARTBEAT_MS while confirmed — the lease LAPSES if we stop,
   *  which is precisely the safety property the boolean confirm lacked. */
  private sendWtHeartbeat(): void {
    if (this.ws.readyState !== WebSocket.OPEN) return;
    this.wtHeartbeatAt = performance.now();
    this.ws.send(JSON.stringify({ t: 'wt', on: true }));
  }

  /** The lane went quiet — WS snapshots resume; the lane may re-promote. */
  private demoteWt(): void {
    if (this.wtState !== 'confirmed') return;
    this.wtState = 'live';
    this.wtSnapsSincePromote = 0;
    netdiag.wtActive = 0;
    netdiag.wtDemotes++;
    if (this.ws.readyState === WebSocket.OPEN)
      this.ws.send(JSON.stringify({ t: 'wt', on: false }));
  }

  private teardownWt(why: 'failed' | 'closed' | 'disposed'): void {
    if (this.wtState === 'confirmed') this.demoteWt(); // tell the server first
    if (this.wtHelloTimer) clearInterval(this.wtHelloTimer);
    this.wtHelloTimer = null;
    this.wtWriter = null;
    const wt = this.wt;
    this.wt = null;
    this.wtState = 'off';
    netdiag.wtActive = 0;
    if (wt && why !== 'closed') {
      try {
        wt.close();
      } catch {
        /* already gone */
      }
    }
    if (why !== 'disposed') this.wtRetryAt = performance.now() + WT_RETRY_MS;
  }

  /** One predicted tick: advance the sim, remember the input for reconcile
   *  replay, and (every few ticks) send the redundant batch. */
  private predictTick(input: RacerInput, extra = false): void {
    if (extra) this.client.predictExtra(input);
    else this.client.predict(input);
    const tick = this.client.predictor.clientTick - 1;
    this.inputRing.push({ tick, input });
    if (this.inputRing.length > INPUT_BATCH_SIZE) this.inputRing.shift();
    if (tick % INPUT_SEND_EVERY === 0) this.sendInputs();
  }

  /** Send the ring: one redundant binary batch (~150 B, 30 a second). M108:
   *  DUAL-SENT on the WT lane when it lives — the server dedupes ticks, so
   *  the second copy is pure loss insurance on whichever path is worse.
   *  M108b: the lane send is NOT gated on the WS being open — a half-open
   *  window (WS dead, radio back) is exactly when the surviving path should
   *  still carry controls. */
  private sendInputs(): void {
    if (this.inputRing.length === 0) return;
    const wsOpen = this.ws.readyState === WebSocket.OPEN;
    const laneUp = (this.wtState === 'live' || this.wtState === 'confirmed') && this.wtWriter;
    if (!wsOpen && !laneUp) return;
    const batch = encodeInputBatch(this.inputRing);
    if (wsOpen) this.ws.send(batch);
    if (laneUp) {
      this.wtWriter?.write(new Uint8Array(batch)).catch(() => {
        /* lane death is handled by `closed` */
      });
    }
  }

  /** Online feel: a use-item edge that landed on a clock-dilation SKIP tick
   *  is carried to the next predicted tick instead of being dropped — the
   *  old path discarded the whole input frame on a skip (a dead button press
   *  the player then repeated; audit #4). */
  private carryUseItem = false;
  /** The last quantized input we predicted with — what a stall catch-up holds. */
  private lastInput: RacerInput = { throttle: 0, steer: 0, useItem: false };

  /**
   * LOCAL STALL COMPENSATION. The render loop did not simulate `n` ticks of
   * real time (a main-thread stall, or a tab that was hidden) — but the
   * server never paused, so our clock must not either. Left alone, the
   * deficit was only discovered a round trip later from the server's
   * late-input reports and worked off slowly: a 0.4 s freeze cost about 1.2 s
   * of late controls (measured in the chaos rig). We KNOW the number, so:
   *
   *  - a short stall is re-simulated right now with the held control, folded
   *    into the render offsets (predictExtra) so it glides out;
   *  - a long one (a hidden tab) only moves the LABEL forward — re-simulating
   *    seconds nobody saw is wasted work; the next snapshot re-derives the
   *    world, holding the last control across the skipped labels exactly as
   *    the server does.
   *
   * Either way the clock stays true to real time, and the controller is told
   * to ignore the "late" feedback our own freeze produced.
   */
  absorbDroppedTicks(n: number): void {
    if (!this.started || n <= 0) return;
    // Only time lost SINCE THE CLOCK WAS SEATED is ours to make up (M143c).
    // The clock is seated from the race's first snapshot, wherever the server
    // is at that moment — so time that passed before then is already in it.
    // A tab hidden BEFORE the race started (a player who locked in and
    // switched away) reported its whole absence on return, and the clock
    // landed that far in the future: every input was refused as "too far
    // ahead" and the car sat dead for as long again (found by review; 8 s
    // hidden before the start = 14 s without controls).
    const now = performance.now();
    n = Math.min(n, Math.floor((now - this.seatedAt) / this.dtMs));
    if (n <= 0) return;
    netdiag.droppedTicks += n;
    const big = n > tuning.net.stallCatchUpMaxTicks;
    if (!big) {
      netdiag.catchUpTicks += n;
      const held: RacerInput = { ...this.lastInput, useItem: false };
      for (let i = 0; i < n; i++) this.predictTick(held, true);
    } else {
      this.sendInputs(); // flush first: an unsent press in the ring must not be lost
      this.client.predictor.skipAhead(n);
      this.inputRing.length = 0; // the ring must stay consecutive-tick
      netdiag.resyncs++;
    }
    // Tell the controller — it decides whether the feedback this stall
    // produced is noise (a one-off) or how this device really runs (chronic).
    this.clock.stalled(this.client.predictor.clientTick, now, big);
  }

  /** Reconcile the newest snapshot received NOW instead of at the next tick
   *  (M143c). Snapshots wait for a tick so a burst costs one replay, not
   *  many — but a driver that is no longer ticking (the player skipped the
   *  spectate view and is on a waiting card) never reaches that tick, and the
   *  results were built from the world as it stood when they stopped
   *  watching. Called right before a result table is built. */
  flushSnapshot(): void {
    const snap = this.pendingSnap;
    if (!snap) return;
    this.pendingSnap = null;
    this.client.applySnapshot(snap);
  }

  tick(localInput: RacerInput): void {
    const now = performance.now();
    // Back from a pause: every ping that was out across it is void (see the pong handler).
    if (now - (this.lastTickAt || this.createdAt) > LOOP_LIVE_MS) this.loopStallEnd = now;
    if (now - this.lastPingAt > 1000) this.sendPing();
    this.lastTickAt = now; // the loop is alive (also in the lobby: see the pong handler)
    if (!this.started) return; // hold in the lobby until the host starts the race
    // WT lane upkeep (M108): a silent confirmed lane demotes (WS resumes); a
    // dead lane retries on a slow cadence while the race runs.
    if (this.wtState === 'confirmed' && now - this.wtSnapAt > WT_DEMOTE_SILENCE_MS) this.demoteWt();
    // M108b: renew the server-side lease while the lane really is delivering.
    if (this.wtState === 'confirmed' && now - this.wtHeartbeatAt > WT_HEARTBEAT_MS)
      this.sendWtHeartbeat();
    if (this.wtState === 'off' && this.wtRetryAt > 0 && now >= this.wtRetryAt) {
      this.wtRetryAt = 0;
      this.startWtLane();
    }
    // M143: the lane measures its own round trip (a datagram echo).
    if (
      (this.wtState === 'live' || this.wtState === 'confirmed') &&
      this.wtWriter &&
      now - this.wtPingAt > WT_PING_MS
    ) {
      this.wtPingAt = now;
      this.wtWriter.write(new Uint8Array(encodeWtPing(now))).catch(() => {
        /* lane death is handled by `closed` */
      });
    }
    // Reconcile the newest snapshot — at most one per tick (M143), and a
    // stale one (a deep replay) only every so often.
    if (this.pendingSnap) {
      const snap = this.pendingSnap;
      const usual = Number.isFinite(this.depthFloor) ? this.depthFloor : 0;
      const deep = this.client.predictor.clientTick - snap.tick - usual > DEEP_REPLAY_TICKS;
      if (!deep || now - this.lastDeepAt >= DEEP_REPLAY_MIN_MS) {
        if (deep) this.lastDeepAt = now;
        this.pendingSnap = null;
        this.client.applySnapshot(snap);
      }
    }
    // Quantize to the wire grid FIRST, and predict with the SAME value the
    // server will apply — otherwise every replay diverges (see codec.ts).
    const input = quantizeInput(localInput);
    if (this.carryUseItem) {
      input.useItem = true;
      this.carryUseItem = false;
    }
    this.lastInput = input;
    // The input buffer (M143): one decision per tick — hold, add ticks, or
    // give one back.
    const act = this.clock.decide();
    if (act?.kind === 'skip') {
      netdiag.skips++;
      if (input.useItem) this.carryUseItem = true; // never drop a press
      this.clock.applied(act, this.client.predictor.clientTick);
      return;
    }
    this.predictTick(input);
    if (act?.kind === 'advance') {
      // Extra ticks predict the SAME held control; the one-shot edge rode the
      // real tick only (the server fires a pending edge exactly once).
      const held: RacerInput = { ...input, useItem: false };
      for (let i = 0; i < act.ticks; i++) this.predictTick(held, true);
      if (act.ticks > BIG_ADVANCE_TICKS) netdiag.resyncs++;
      else netdiag.doubles += act.ticks;
      this.clock.applied(act, this.client.predictor.clientTick);
    }
  }

  poses(alpha: number, frameDt: number): RacerPose[] {
    return this.client.poses(alpha, frameDt);
  }

  get racers(): SimRacer[] {
    return this.client.predictor.racers;
  }

  get player(): SimRacer {
    return this.client.predictor.local();
  }

  /** Online feel: the LOCAL player's own beats (fire / deny / collect /
   *  ball hit / pad / item land) and the countdown come from the PREDICTED
   *  world — instant — and the server's copies of those same events are
   *  dropped. Everything else (rivals' fires, hits on us, finishes) stays
   *  server-authoritative. `localId` is the seat we predict for. */
  takeEvents(): SimEvent[] {
    const local = this.client.predictor.takeLocalEvents();
    const server = this.pendingEvents;
    this.pendingEvents = [];
    const out: SimEvent[] = [];
    for (const e of local) if (isLocallyPredicted(e, this.localId)) out.push(e);
    for (const e of server) if (!isLocallyPredicted(e, this.localId)) out.push(e);
    return out;
  }

  /** The server owns the race; a fresh race = a fresh connection. */
  reset(): void {}

  /** Detach from the shared socket — Game owns the ws (it survives a rematch),
   *  so a disposed driver must NOT close it; it only stops listening. The WT
   *  lane however is OURS: it dies with the driver. */
  dispose(): void {
    this.disposed = true;
    this.ws.removeEventListener('message', this.onMsg);
    this.teardownWt('disposed');
  }
}
